import { Holiday, TimeOffRequest } from '../types'

class DateCalculationService {
  private parseDateOnly(dateStr: string): Date {
    const [year, month, day] = dateStr.split('-').map(Number)
    return new Date(Date.UTC(year, month - 1, day))
  }

  normalizeToUTCDateOnly(date: Date): Date {
    return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()))
  }

  private formatUTC(date: Date, options: Intl.DateTimeFormatOptions): string {
    return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()))
      .toLocaleDateString('en-US', { ...options, timeZone: 'UTC' })
  }

  private toDateOnlyString(date: Date): string {
    return date.toISOString().substring(0, 10)
  }

  isWeekend(date: Date): boolean {
    const d = date.getUTCDay()
    return d === 0 || d === 6
  }

  isHoliday(date: Date, holidays: Holiday[]): boolean {
    const s = this.toDateOnlyString(date)
    return holidays.some(h => h.date === s)
  }

  isWorkingDay(date: Date, holidays: Holiday[]): boolean {
    return !this.isWeekend(date) && !this.isHoliday(date, holidays)
  }

  getWorkingDays(startDate: string, endDate: string, holidays: Holiday[]): number {
    const start = this.parseDateOnly(startDate)
    const end = this.parseDateOnly(endDate)
    if (start > end) return 0
    let count = 0
    const cur = new Date(start.getTime())
    while (cur <= end) {
      if (this.isWorkingDay(cur, holidays)) count++
      cur.setUTCDate(cur.getUTCDate() + 1)
    }
    return count
  }

  getWorkingDaysInMonth(year: number, month: number, holidays: Holiday[]): number {
    const start = new Date(Date.UTC(year, month, 1))
    const end = new Date(Date.UTC(year, month + 1, 0))
    return this.getWorkingDays(this.toDateOnlyString(start), this.toDateOnlyString(end), holidays)
  }

  getWorkingDaysInMonthOverlap(allocationStart: string, allocationEnd: string, year: number, month: number, holidays: Holiday[]): number {
    const monthStart = new Date(Date.UTC(year, month, 1))
    const monthEnd = new Date(Date.UTC(year, month + 1, 0))
    const allocStart = this.parseDateOnly(allocationStart)
    const allocEnd = this.parseDateOnly(allocationEnd)
    const overlapStart = new Date(Math.max(allocStart.getTime(), monthStart.getTime()))
    const overlapEnd = new Date(Math.min(allocEnd.getTime(), monthEnd.getTime()))
    if (overlapStart > overlapEnd) return 0
    return this.getWorkingDays(this.toDateOnlyString(overlapStart), this.toDateOnlyString(overlapEnd), holidays)
  }

  getWorkingHours(startDate: string, endDate: string, dailyHours: number, holidays: Holiday[]): number {
    return this.getWorkingDays(startDate, endDate, holidays) * dailyHours
  }

  getWorkingDaysList(startDate: string, endDate: string, holidays: Holiday[]): string[] {
    const start = this.parseDateOnly(startDate)
    const end = this.parseDateOnly(endDate)
    const result: string[] = []
    if (start > end) return result
    const cur = new Date(start.getTime())
    while (cur <= end) {
      if (this.isWorkingDay(cur, holidays)) result.push(this.toDateOnlyString(cur))
      cur.setUTCDate(cur.getUTCDate() + 1)
    }
    return result
  }

  getStartOfWeek(date: Date): Date {
    const n = this.normalizeToUTCDateOnly(date)
    const day = n.getUTCDay()
    const start = new Date(n.getTime())
    start.setUTCDate(start.getUTCDate() - day)
    return start
  }

  getEndOfWeek(date: Date): Date {
    const n = this.normalizeToUTCDateOnly(date)
    const day = n.getUTCDay()
    const end = new Date(n.getTime())
    end.setUTCDate(end.getUTCDate() + (6 - day))
    end.setUTCHours(23, 59, 59, 999)
    return end
  }

  getStartOfMonth(date: Date): Date {
    return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1))
  }

  getEndOfMonth(date: Date): Date {
    const r = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 0))
    r.setUTCHours(23, 59, 59, 999)
    return r
  }

  getStartOfQuarter(date: Date): Date {
    const q = Math.floor(date.getUTCMonth() / 3)
    return new Date(Date.UTC(date.getUTCFullYear(), q * 3, 1))
  }

  getEndOfQuarter(date: Date): Date {
    const q = Math.floor(date.getUTCMonth() / 3)
    return new Date(Date.UTC(date.getUTCFullYear(), q * 3 + 3, 0))
  }

  getStartOfYear(date: Date): Date {
    return new Date(Date.UTC(date.getUTCFullYear(), 0, 1))
  }

  getEndOfYear(date: Date): Date {
    return new Date(Date.UTC(date.getUTCFullYear(), 11, 31))
  }

  getWorkingDaysInRange(startDate: Date, endDate: Date, holidays: Holiday[]): number {
    const start = new Date(Date.UTC(startDate.getUTCFullYear(), startDate.getUTCMonth(), startDate.getUTCDate()))
    const end = new Date(Date.UTC(endDate.getUTCFullYear(), endDate.getUTCMonth(), endDate.getUTCDate()))
    if (start > end) return 0
    let count = 0
    const cur = new Date(start.getTime())
    while (cur <= end) {
      if (this.isWorkingDay(cur, holidays)) count++
      cur.setUTCDate(cur.getUTCDate() + 1)
    }
    return count
  }

  formatDateRange(startDate: Date, endDate: Date, viewType: string): string {
    startDate = this.normalizeToUTCDateOnly(startDate)
    endDate = this.normalizeToUTCDateOnly(endDate)
    if (viewType === 'day') return this.formatUTC(startDate, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })
    if (viewType === 'week') return `${this.formatUTC(startDate, { month: 'short', day: 'numeric' })} - ${this.formatUTC(endDate, { month: 'short', day: 'numeric' })}`
    if (viewType === 'month') return this.formatUTC(startDate, { month: 'long', year: 'numeric' })
    if (viewType === 'quarter') return `Q${Math.floor(startDate.getUTCMonth() / 3) + 1} ${startDate.getUTCFullYear()}`
    if (viewType === 'year') return startDate.getUTCFullYear().toString()
    return `${this.formatUTC(startDate, { month: 'short', day: 'numeric' })} - ${this.formatUTC(endDate, { month: 'short', day: 'numeric' })}`
  }

  // Expands approved time-off requests for a specific resource into a Set of
  // individual date strings (YYYY-MM-DD) within an optional date range filter.
  getResourceTimeOffDateSet(
    resourceId: string,
    timeOffRequests: TimeOffRequest[],
    rangeStart?: string,
    rangeEnd?: string
  ): Set<string> {
    const dates = new Set<string>()
    const rangeStartDate = rangeStart ? this.parseDateOnly(rangeStart) : null
    const rangeEndDate = rangeEnd ? this.parseDateOnly(rangeEnd) : null

    timeOffRequests
      .filter(r => r.resource_id === resourceId && r.status === 'approved')
      .forEach(r => {
        const start = this.parseDateOnly(r.start_date)
        const end = this.parseDateOnly(r.end_date)
        const cur = new Date(start.getTime())
        while (cur <= end) {
          if ((!rangeStartDate || cur >= rangeStartDate) && (!rangeEndDate || cur <= rangeEndDate)) {
            dates.add(this.toDateOnlyString(cur))
          }
          cur.setUTCDate(cur.getUTCDate() + 1)
        }
      })

    return dates
  }

  // Like isWorkingDay but also excludes days in an approved time-off set.
  isAvailableDay(date: Date, holidays: Holiday[], timeOffDates: Set<string>): boolean {
    return this.isWorkingDay(date, holidays) && !timeOffDates.has(this.toDateOnlyString(date))
  }

  // Working days minus approved time-off for a specific resource in a range.
  getAvailableWorkingDays(
    startDate: string,
    endDate: string,
    holidays: Holiday[],
    timeOffDates: Set<string>
  ): number {
    const start = this.parseDateOnly(startDate)
    const end = this.parseDateOnly(endDate)
    if (start > end) return 0
    let count = 0
    const cur = new Date(start.getTime())
    while (cur <= end) {
      if (this.isAvailableDay(cur, holidays, timeOffDates)) count++
      cur.setUTCDate(cur.getUTCDate() + 1)
    }
    return count
  }

  getAvailableWorkingDaysInRange(
    startDate: Date,
    endDate: Date,
    holidays: Holiday[],
    timeOffDates: Set<string>
  ): number {
    const start = new Date(Date.UTC(startDate.getUTCFullYear(), startDate.getUTCMonth(), startDate.getUTCDate()))
    const end = new Date(Date.UTC(endDate.getUTCFullYear(), endDate.getUTCMonth(), endDate.getUTCDate()))
    if (start > end) return 0
    let count = 0
    const cur = new Date(start.getTime())
    while (cur <= end) {
      if (this.isAvailableDay(cur, holidays, timeOffDates)) count++
      cur.setUTCDate(cur.getUTCDate() + 1)
    }
    return count
  }

  getWeekNumber(date: Date): number {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()))
    d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7))
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1))
    return Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7)
  }
}

export const dateCalculationService = new DateCalculationService()
